export type AssetType = 'image' | 'audio' | 'document' | 'video' | 'subtitle' | 'web';

export type ProcessingState = 'ready' | 'processing' | 'failed';

/** Full manager, or the picker a course editor opens in a dialog. Fixed for the session. */
export type LibraryMode = 'manage' | 'project';

/**
 * `type` and `extension` are derived from `name` on the way in rather than sent: the extension
 * decides both, and duplicating that on the wire only invites the two to disagree.
 */
export interface AssetListItem {
  id: string;
  name: string;
  type: AssetType;
  extension: string;
  size: number;
  uploadDate: Date;
  state: ProcessingState;
  /** Only a listing row has this: the server reports it from the walk that scopes the query. */
  folderId?: string;
  thumbnailUrl?: string;
}

/**
 * Only a ready asset can be selected. One still processing has nothing yet that a host could use
 * or a bulk action could act on, and a failed one never will -- Replace or Delete, from its own
 * menu, are what is left for it.
 */
export const isSelectableAsset = (asset: Pick<AssetListItem, 'state'>): boolean => asset.state === 'ready';

/** A superset of `AssetListItem`, so one write's response can patch both caches. */
export interface AssetDetail extends AssetListItem {
  /** Opens the file inline, for the preview. */
  mediaUrl: string;
  /** Saves the file under the asset's name. */
  downloadUrl: string;
  /** When both URLs stop working; a detail held past this has to be fetched again. */
  urlsExpireAt: Date;
  imageWidth?: number;
  imageHeight?: number;
  attribution?: AssetAttribution;
}

/**
 * Credit an asset's source requires, e.g. an Unsplash photographer.
 *
 * Whole-or-nothing, as the server publishes it, so the caption tests one field rather than four.
 * Only on the detail: a listing row draws no credit, and the preview is what fetches a detail.
 */
export interface AssetAttribution {
  authorName: string;
  authorUrl: string;
  source: string;
  sourceUrl: string;
}

/**
 * What a host is handed when the user picks a file in `project` mode.
 *
 * Deliberately only what a listing row already knows. The download URL lives on `AssetDetail`, so
 * including it would make every pick fetch a detail first -- and hosts reference assets by id
 * anyway, which is what survives a later replace of the file behind it.
 */
export interface PickedAsset {
  id: string;
  name: string;
}

/** A page of the asset listing, with the number of matches behind it. */
export interface AssetPage {
  /** How many assets match the scope in total, before paging. */
  total: number;
  items: readonly AssetListItem[];
}

/**
 * One folder, as the server lists it: flat, pointing at its parent.
 *
 * `parentId` is `null` for a top-level folder. The library root is not in the list -- the UI
 * draws it as the root -- so `buildFolderTree` is what attaches these to it.
 */
export interface Folder {
  id: string;
  name: string;
  parentId: string | null;
}

export type SortField = 'uploadDate' | 'name' | 'size';
export type SortDirection = 'asc' | 'desc';

export interface SortOption {
  field: SortField;
  direction: SortDirection;
  label: string;
}

export const SORT_OPTIONS: SortOption[] = [
  { field: 'uploadDate', direction: 'desc', label: 'Newest first' },
  { field: 'uploadDate', direction: 'asc', label: 'Oldest first' },
  { field: 'name', direction: 'asc', label: 'Name A→Z' },
  { field: 'name', direction: 'desc', label: 'Name Z→A' },
  { field: 'size', direction: 'desc', label: 'Size (largest)' },
  { field: 'size', direction: 'asc', label: 'Size (smallest)' },
];

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  image: 'Image',
  audio: 'Audio',
  document: 'Document',
  video: 'Video',
  subtitle: 'Subtitle',
  web: 'Web',
};

const EXTENSION_TO_TYPE: Record<string, AssetType> = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  svg: 'image',
  mp3: 'audio',
  pdf: 'document',
  mp4: 'video',
  mov: 'video',
  vtt: 'subtitle',
  html: 'web',
};

/** Every type the library knows, in the order the toolbar offers them. */
export const ALL_ASSET_TYPES: readonly AssetType[] = ['image', 'audio', 'video', 'document', 'subtitle', 'web'];

/** The extensions a file input should accept for these types, e.g. `.jpg,.png`. */
export function acceptAttributeFor(types: readonly AssetType[]): string {
  return Object.entries(EXTENSION_TO_TYPE)
    .filter(([, type]) => types.includes(type))
    .map(([extension]) => `.${extension}`)
    .join(',');
}

export function getAssetType(filename: string): AssetType | null {
  const ext = getExtension(filename);
  if (!ext) return null;
  return EXTENSION_TO_TYPE[ext] ?? null;
}

/**
 * The extension, lowercased, or `''` when the name has none.
 *
 * `split('.').pop()` returns the *whole name* for a name with no dot — "README" would report an
 * extension of "readme" — which then reaches `EXTENSION_TO_TYPE` as a lookup that can only miss,
 * and reaches the UI as a suffix the file does not have. A leading dot is not an extension either:
 * ".gitignore" is a name, so `lastIndexOf` has to find the dot somewhere after the first character.
 */
export function getExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

/**
 * Splits an asset's name into the part a user may rename and the extension they may not.
 *
 * The extension is part of the asset's identity -- it decides the type icon, what the preview
 * does, what Replace will accept, and it is in every link a course holds -- so renaming should not
 * be able to change it.
 *
 * Conditional, because `name` need not end with `extension`: the server keeps the display name and
 * the filename apart, so "Cockpit overview" with extension `png` is ordinary. A suffix is split off
 * only when the name genuinely ends with it, and it keeps the name's own casing rather than the
 * normalised extension, so "photo.PNG" stays "photo.PNG".
 */
export function splitAssetName(name: string, extension: string): { base: string; suffix: string } {
  if (!extension) return { base: name, suffix: '' };

  const suffixLength = extension.length + 1;
  const candidate = name.slice(-suffixLength);
  if (candidate.toLowerCase() !== `.${extension.toLowerCase()}`) return { base: name, suffix: '' };

  const base = name.slice(0, -suffixLength);
  // A name that is only an extension (".gitignore") has no base to edit, so leave it whole rather
  // than offering an empty input.
  if (base.length === 0) return { base: name, suffix: '' };

  return { base, suffix: candidate };
}

/** Puts back together what {@link splitAssetName} separated, once the base has been edited. */
export function joinAssetName(base: string, suffix: string): string {
  return `${base}${suffix}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * A playing time as a player shows it: `m:ss` under an hour, `h:mm:ss` from there. Rounded to the
 * nearest second, so a 2.6 s clip reads `0:03` rather than claiming to be shorter than it is.
 */
export function formatDuration(totalSeconds: number): string {
  const rounded = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = String(rounded % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
